import { useState } from 'react';
import {
  Stepper,
  Step,
  StepLabel,
  StepButton,
  IconButton,
  Typography,
  makeStyles,
  Chip,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { PipelineDefinition, Phase, ActivityStatus } from '../../api/types';

export interface PhaseStepperProps {
  pipelineDefinition: PipelineDefinition;
  currentPhase: Phase;
  selectedPhase: Phase;
  activityStatus: ActivityStatus;
  onPhaseClick: (phase: Phase) => void;
}

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(0.25, 0.5),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(0.5),
  },
  collapsedRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 0.5),
    cursor: 'pointer',
    userSelect: 'none',
  },
  collapsedLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  collapsedLabel: {
    fontWeight: 600,
    fontSize: '0.75rem',
    color: theme.palette.text.primary,
  },
  collapsedCounter: {
    fontWeight: 400,
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
  },
  collapsedRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  miniProgressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    width: 80,
  },
  miniProgressSegment: {
    flex: 1,
    height: 3,
    borderRadius: 1.5,
  },
  miniProgressCompleted: {
    backgroundColor: theme.palette.secondary.main,
  },
  miniProgressActive: {
    backgroundColor: theme.palette.secondary.main,
    opacity: 0.5,
  },
  miniProgressPending: {
    backgroundColor: theme.palette.grey[300],
  },
  scrollContainer: {
    overflowX: 'auto',
    overflowY: 'hidden',
    '&::-webkit-scrollbar': {
      height: 4,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 2,
      backgroundColor: theme.palette.divider,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
  },
  root: {
    padding: theme.spacing(0.25, 0),
    backgroundColor: 'transparent',
    minWidth: 'max-content',
    width: '100%',
    '& .MuiStep-root': {
      padding: theme.spacing(0, 0.5),
    },
    '& .MuiStepConnector-line': {
      minHeight: 0,
    },
  },
  activeChip: {
    marginLeft: theme.spacing(1),
  },
  stepLabel: {
    '& .MuiStepLabel-label': {
      fontSize: '0.72rem',
      [theme.breakpoints.down('sm')]: {
        fontSize: '0.75rem',
      },
    },
  },
}));

export function getPhaseStatus(
  phase: Phase,
  currentPhase: Phase,
  phaseOrder: Phase[],
  activityStatus: ActivityStatus,
): 'completed' | 'active' | 'pending' {
  if (activityStatus === 'completed') return 'completed';
  const currentIdx = phaseOrder.indexOf(currentPhase);
  const phaseIdx = phaseOrder.indexOf(phase);
  if (phaseIdx < currentIdx) return 'completed';
  if (phaseIdx === currentIdx) return 'active';
  return 'pending';
}

function PhaseStepIcon({
  status,
}: {
  status: 'completed' | 'active' | 'pending';
}) {
  const iconStyle = { fontSize: '1rem' };
  switch (status) {
    case 'completed':
      return <CheckCircleIcon color="primary" style={iconStyle} />;
    case 'active':
      return <FiberManualRecordIcon color="secondary" style={iconStyle} />;
    default:
      return <RadioButtonUncheckedIcon color="disabled" style={iconStyle} />;
  }
}

export const PhaseStepper = ({
  pipelineDefinition,
  currentPhase,
  selectedPhase,
  activityStatus,
  onPhaseClick,
}: PhaseStepperProps) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const phaseOrder = pipelineDefinition.phases.map(phase => phase.id);
  const phaseLabels = pipelineDefinition.phases.reduce<Record<Phase, string>>(
    (acc, phase) => {
      acc[phase.id] = phase.label;
      return acc;
    },
    {} as Record<Phase, string>,
  );
  const activeStepIndex = phaseOrder.indexOf(selectedPhase);
  const currentIndex = phaseOrder.indexOf(currentPhase);
  const totalPhases = phaseOrder.length;
  const currentPhaseNumber = currentIndex + 1;

  return (
    <div className={classes.container}>
      <div
        className={classes.collapsedRow}
        onClick={() => setExpanded(prev => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded(prev => !prev);
          }
        }}
        aria-expanded={expanded}
        aria-label={`${phaseLabels[currentPhase]} - ${currentPhaseNumber} de ${totalPhases}`}
      >
        <div className={classes.collapsedLeft}>
          <PhaseStepIcon
            status={getPhaseStatus(
              currentPhase,
              currentPhase,
              phaseOrder,
              activityStatus,
            )}
          />
          <Typography className={classes.collapsedLabel} component="span">
            {phaseLabels[currentPhase]}
          </Typography>
          <Typography className={classes.collapsedCounter} component="span">
            {currentPhaseNumber} de {totalPhases}
          </Typography>
        </div>
        <div className={classes.collapsedRight}>
          <div className={classes.miniProgressContainer}>
            {phaseOrder.map(phase => {
              const status = getPhaseStatus(
                phase,
                currentPhase,
                phaseOrder,
                activityStatus,
              );
              let segmentClass = classes.miniProgressPending;
              if (status === 'completed')
                segmentClass = classes.miniProgressCompleted;
              else if (status === 'active')
                segmentClass = classes.miniProgressActive;
              return (
                <div
                  key={phase}
                  className={`${classes.miniProgressSegment} ${segmentClass}`}
                />
              );
            })}
          </div>
          <IconButton
            size="small"
            aria-label={expanded ? 'Colapsar' : 'Expandir'}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </div>
      </div>

      {expanded && (
        <div className={classes.scrollContainer}>
          <Stepper
            activeStep={activeStepIndex}
            alternativeLabel
            nonLinear
            className={classes.root}
          >
            {phaseOrder.map(phase => {
              const status = getPhaseStatus(
                phase,
                currentPhase,
                phaseOrder,
                activityStatus,
              );
              const isClickable = status === 'completed' || status === 'active';
              const isSelected = phase === selectedPhase;

              return (
                <Step key={phase} completed={status === 'completed'}>
                  {isClickable ? (
                    <StepButton
                      className={classes.stepLabel}
                      onClick={() => onPhaseClick(phase)}
                      icon={<PhaseStepIcon status={status} />}
                    >
                      {phaseLabels[phase]}
                      {isSelected && status === 'active' && (
                        <Chip
                          size="small"
                          label="Atual"
                          color="secondary"
                          className={classes.activeChip}
                        />
                      )}
                    </StepButton>
                  ) : (
                    <StepLabel
                      className={classes.stepLabel}
                      icon={<PhaseStepIcon status={status} />}
                    >
                      {phaseLabels[phase]}
                    </StepLabel>
                  )}
                </Step>
              );
            })}
          </Stepper>
        </div>
      )}
    </div>
  );
};
